import React, { useEffect, useRef } from 'react';

export default function MeteorShower() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    let meteors = [];

    const getPageHeight = () => Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = getPageHeight();
      // Redistribute meteors across new full height
      meteors.forEach(m => m.reassign(canvas.width, canvas.height));
    };

    class Meteor {
      constructor(w, h) { this.w = w; this.h = h; this.reset(true); }

      reassign(w, h) { this.w = w; this.h = h; }

      reset(initial = false) {
        // Spawn across full width AND full page height
        this.x = Math.random() * this.w;
        this.y = initial
          ? Math.random() * this.h          // scatter randomly on load
          : Math.random() * this.h * 0.3;   // re-enter from top third
        this.len   = Math.random() * 130 + 60;
        this.speed = Math.random() * 5 + 2;
        this.angle = (Math.PI / 4) + (Math.random() * 0.4 - 0.2);
        this.width = Math.random() * 2.5 + 0.5;
        this.alpha = Math.random() * 0.6 + 0.4;
        const hue  = Math.random() * 35 + 5;
        this.colorHead = `hsla(${hue + 20}, 100%, 92%, ${this.alpha})`;
        this.colorTail = `hsla(${hue}, 100%, 45%, 0)`;
        this.glowColor = `hsla(${hue}, 100%, 55%, 0.35)`;
      }

      update() {
        this.x += Math.cos(this.angle) * this.speed;
        this.y += Math.sin(this.angle) * this.speed;
        // Reset when it exits the page on any edge
        if (this.x > this.w + 150 || this.y > this.h + 150) {
          this.reset(false);
        }
      }

      draw(ctx) {
        const tailX = this.x - Math.cos(this.angle) * this.len;
        const tailY = this.y - Math.sin(this.angle) * this.len;

        // Outer glow
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(this.x, this.y);
        ctx.strokeStyle = this.glowColor;
        ctx.lineWidth = this.width * 5;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.restore();

        // Main trail gradient
        const grad = ctx.createLinearGradient(tailX, tailY, this.x, this.y);
        grad.addColorStop(0, this.colorTail);
        grad.addColorStop(1, this.colorHead);

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(this.x, this.y);
        ctx.strokeStyle = grad;
        ctx.lineWidth = this.width;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.restore();

        // Bright tip
        ctx.save();
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.width * 2, 0, Math.PI * 2);
        ctx.fillStyle = this.colorHead;
        ctx.fill();
        ctx.restore();
      }
    }

    // Initial setup
    resize();
    meteors = Array.from({ length: 40 }, () => new Meteor(canvas.width, canvas.height));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      meteors.forEach(m => { m.update(); m.draw(ctx); });
      animId = requestAnimationFrame(animate);
    };
    animate();

    // Watch for page height changes as React renders more content
    const ro = new ResizeObserver(() => {
      const newH = getPageHeight();
      if (Math.abs(canvas.height - newH) > 50) resize();
    });
    ro.observe(document.body);
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        pointerEvents: 'none',
        zIndex: 0,
        opacity: 0.6,
      }}
    />
  );
}
